import type { LayoutConfig } from '@/utils/dealCalculations'
import { findCategoryCoords } from '@/utils/dealCalculations'
import {
  CATEGORY_BLOCK_NUMBER_START,
  FIXED_SLIDE_LAYOUT_BLOCKS,
  type FixedLayoutBlockDef,
  ZONE_LABELS,
} from './fixedSlideLayoutBlocks'

export type {
  FixedLayoutBlockId,
  FixedLayoutBlockDef,
} from './fixedSlideLayoutBlocks'

export {
  FIXED_SLIDE_LAYOUT_BLOCKS,
  CATEGORY_BLOCK_NUMBER_START,
  FIXED_BLOCK_BY_ID,
} from './fixedSlideLayoutBlocks'

export function getZoneLabel(zone: FixedLayoutBlockDef['zone']): string {
  return ZONE_LABELS[zone]
}

export function getFixedBlockValue(layout: LayoutConfig, block: FixedLayoutBlockDef): number {
  if (!block.layoutKey) return block.defaultValue
  const raw = layout[block.layoutKey]
  return typeof raw === 'number' ? raw : block.defaultValue
}

export function formatLayoutPercent(value: number): string {
  return `${Math.round(value * 10) / 10}%`
}

const LINE_HEIGHT_PCT = 3.1
const BLOCK_HEADER_PCT = 5

export type ScopeCategoryInput = {
  category?: string
  categoria?: string
  services?: Array<{ isIncluded?: boolean; name?: string; nome?: string; quantity?: string | number; quantidade?: string | number }>
}

function categoryBlockHeight(itemCount: number): number {
  return BLOCK_HEADER_PCT + Math.max(1, itemCount) * LINE_HEIGHT_PCT
}

/** Recalcula posições das categorias sem sobreposição (colunas alternadas). */
export function rebuildCategoryCoordsFromScope(
  serviceCategories: ScopeCategoryInput[],
  layout: Pick<LayoutConfig, 'serviceBlockX' | 'serviceBlockGap' | 'serviceBlockStartY'>,
): Record<string, { x: number; y: number }> {
  const col1X = layout.serviceBlockX ?? 38
  const col2X = layout.serviceBlockGap ?? 68
  const startY = layout.serviceBlockStartY ?? 10
  let leftY = startY
  let rightY = startY
  const coords: Record<string, { x: number; y: number }> = {}

  serviceCategories.forEach((cat, idx) => {
    const name = (cat.category || cat.categoria || 'Geral').trim()
    const included = (cat.services || []).filter((s) => s.isIncluded !== false)
    if (!name || included.length === 0) return

    const isLeft = idx % 2 === 0
    const h = categoryBlockHeight(included.length)
    const x = isLeft ? col1X : col2X
    const y = isLeft ? leftY : rightY
    coords[name] = { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 }

    if (isLeft) leftY += h + 1.5
    else rightY += h + 1.5
  })

  return coords
}

export function getScopeLayoutSignature(serviceCategories: ScopeCategoryInput[]): string {
  return JSON.stringify(
    serviceCategories.map((cat) => ({
      cat: cat.category || cat.categoria || '',
      items: (cat.services || [])
        .filter((s) => s.isIncluded !== false)
        .map((s) => s.name || s.nome || ''),
    })),
  )
}

export interface CategoryLayoutBlock {
  number: number
  category: string
  x: number
  y: number
}

export function buildCategoryLayoutBlocks(
  layout: LayoutConfig,
  categoryNames: string[],
): CategoryLayoutBlock[] {
  const col1 = layout.serviceBlockX || 38
  const col2 = layout.serviceBlockGap || 68
  const startY = layout.serviceBlockStartY || 10

  return categoryNames.map((category, idx) => {
    const saved = findCategoryCoords(layout.categoryCoords, category)
    const isLeft = idx % 2 === 0
    return {
      number: CATEGORY_BLOCK_NUMBER_START + idx,
      category,
      x: saved?.x ?? (isLeft ? col1 : col2),
      y: saved?.y ?? startY,
    }
  })
}

export function formatLayoutTemplate(
  layout: LayoutConfig,
  categoryNames: string[],
): string {
  const lines: string[] = [
    '# Template — Slide Investimento (16:9)',
    '# Referência por número de bloco. Valores em % do slide (X = horizontal, Y = vertical, L = largura).',
    '',
  ]

  for (const block of FIXED_SLIDE_LAYOUT_BLOCKS) {
    const v = getFixedBlockValue(layout, block)
    if (block.axis === 'width') {
      lines.push(`Bloco ${block.number} — ${block.label}: L ${formatLayoutPercent(v)}`)
    } else if (block.axis === 'y') {
      lines.push(`Bloco ${block.number} — ${block.label}: Y ${formatLayoutPercent(v)}`)
    } else {
      lines.push(`Bloco ${block.number} — ${block.label}: X ${formatLayoutPercent(v)}`)
    }
  }

  const categories = buildCategoryLayoutBlocks(layout, categoryNames)
  if (categories.length > 0) {
    lines.push('')
    lines.push('# Categorias de entregáveis')
    for (const cat of categories) {
      lines.push(
        `Bloco ${cat.number} — ${cat.category}: X ${formatLayoutPercent(cat.x)}, Y ${formatLayoutPercent(cat.y)}`,
      )
    }
  }

  lines.push('')
  lines.push('# Exemplo de pedido ao assistente:')
  lines.push('# "Ajuste o template: Bloco 6 Y 78%, Bloco 10 X 36%, Bloco 13 X 38% Y 12%"')

  return lines.join('\n')
}
